import React from "react";
import { Box, TextField, MenuItem, Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import VideocamIcon from "@material-ui/icons/Videocam";
import { MUTED, G_SOFT, ACCOUNT, ACCOUNT_SOFT, CONTACT, CONTACT_SOFT } from "./tokens";
import {
  ADDON_FAMILIES, familyLabel, variantsAtDuration, findByKey, unitOf,
  totalUnitsOf, flowPacksAt, flowAddOnsFor, incrementPackCount,
} from "./checkoutCatalog";
import { allows, entitlementsFor, ADDON_DESC } from "./studioConstants";
import { IssueBanner } from "./StepSection";

// Flows sell as one base pack plus N increment packs, so the reachable totals
// are 5, 15, 25, … and the control steps by whole packs.
const FlowStepper = ({ classes, catalog, sub, locked, onPatchSub }) => {
  const packs = flowPacksAt(catalog, sub.duration);
  if (!packs) {
    return <div className={classes.errText}>No flow packs are available for {sub.duration}.</div>;
  }
  const k = incrementPackCount(packs, sub.addOns);
  const total = totalUnitsOf(catalog, sub.addOns, "flow_addon");
  const unit = unitOf(catalog, "flow_addon") || "unit";
  const setK = (next) => {
    if (next < 0) return;
    onPatchSub({
      addOns: [
        ...(sub.addOns || []).filter((a) => a.family !== "flow_addon"),
        ...flowAddOnsFor(packs, next),
      ],
    });
  };
  return (
    <Box className={classes.stepper}>
      <button type="button" className={classes.stepBtn} disabled={locked || k === 0}
        aria-label={`Remove one ${packs.increment.size}-${unit} pack`}
        onClick={() => setK(k - 1)}>−</button>
      <Box className={classes.stepVal}>
        <div className={classes.stepNum}>{total.toLocaleString("en-IN")} {unit}s</div>
        <div className={classes.stepSub}>
          base {packs.base.size}{k > 0 ? ` + ${k} × ${packs.increment.size}` : ""}
        </div>
      </Box>
      <button type="button" className={classes.stepBtn} disabled={locked}
        aria-label={`Add one ${packs.increment.size}-${unit} pack`}
        onClick={() => setK(k + 1)}>+</button>
    </Box>
  );
};

// Controls for one enabled add-on family. Flows get a stepper, seats a count,
// and AI messages a pack picker — their packs are discrete priced choices,
// not an additive ladder.
const AddonBody = ({
  family, classes, catalog, sub, entry, locked, lockMark, showErrors,
  onPatchSub, onSetQty, onSetVariant,
}) => {
  if (!entry) return null;

  if (family === "flow_addon") {
    return <FlowStepper classes={classes} catalog={catalog} sub={sub} locked={locked} onPatchSub={onPatchSub} />;
  }

  if (family === "team_members") {
    return (
      <div>
        <span className={classes.fieldLabel}>Seats{lockMark()}</span>
        <TextField className={classes.input} type="number" variant="outlined" size="small"
          style={{ width: 120 }} inputProps={{ min: 1 }}
          disabled={locked}
          error={showErrors && !(Number(entry.quantity) >= 1)}
          value={entry.quantity}
          onChange={(e) => onSetQty(family, e.target.value)} />
      </div>
    );
  }

  return (
    <div>
      <span className={classes.fieldLabel}>Pack{lockMark()}</span>
      <TextField className={classes.input} select fullWidth variant="outlined" size="small"
        disabled={locked}
        value={(findByKey(catalog, entry.key) || {}).variant || ""}
        onChange={(e) => onSetVariant(family, e.target.value)}>
        {variantsAtDuration(catalog, family, sub.duration).map((v) => (
          <MenuItem key={v.variant} value={v.variant}>{v.label}</MenuItem>
        ))}
      </TextField>
    </div>
  );
};

// Step 2 — what the customer actually receives. Quantities only; every price is
// entered in step 3.
const EntitlementsStep = ({
  classes, catalog, form, sub, credits, webinar, creditFields,
  singleEnt, locked, lockMark, showErrors, shapeErr,
  onToggleSub, onToggleCredits, onToggleWebinar,
  planVariant, onSetPlanVariant,
  addonOf, onAddAddon, onRemoveAddon, onSetAddonVariant, onSetAddonQty, onPatchSub,
  onPatchCredits, onPatchWebinar,
  issues, onBack, onContinue,
}) => (
  <>
    {locked && (
      <div className={classes.lockBanner}>
        <LockOutlinedIcon style={{ fontSize: 14, color: MUTED, flex: "none" }} />
        <span>Fixed after creation. Archive this product to change what it sells.</span>
      </div>
    )}

    {/* Subscription */}
    {allows(form.type, "subscription") && (
    <Box className={`${classes.ent} ${sub ? classes.entOn : ""} ${locked ? classes.entLocked : ""}`}>
      <Box className={classes.entHead} onClick={locked ? undefined : onToggleSub}>
        <Box className={classes.entIco} style={{ background: ACCOUNT_SOFT, color: ACCOUNT }}><AutorenewIcon style={{ fontSize: 20 }} /></Box>
        <Box>
          <Box className={classes.entName}>Subscription
            <span className={classes.when} style={{ color: ACCOUNT, background: ACCOUNT_SOFT }}>after they redeem</span></Box>
          <Box className={classes.entDesc}>A recurring plan that renews each term. Plan and add-ons bill together.</Box>
        </Box>
        <Box className={`${classes.sw} ${sub ? classes.swOn : ""} ${locked ? classes.swDead : ""}`}><span className={`${classes.knob} ${sub ? classes.knobOn : ""}`} /></Box>
      </Box>
      {sub && (
        <Box className={classes.entBody}>
          <div className={classes.hint}>
            Duration: <strong>{sub.duration}</strong> — set by the billing period in step 1. Plan and add-ons all bill on this term.
          </div>
          <div>
            <span className={classes.fieldLabel}>Plan{lockMark()}</span>
            <TextField className={classes.input} select fullWidth variant="outlined" size="small"
              disabled={locked}
              value={planVariant} onChange={(e) => onSetPlanVariant(e.target.value)}>
              <MenuItem value=""><em>None (add-ons only)</em></MenuItem>
              {variantsAtDuration(catalog, "plan", sub.duration).map((v) => (
                <MenuItem key={v.variant} value={v.variant}>{v.label}</MenuItem>
              ))}
            </TextField>
          </div>

          <Box className={classes.subHead}>
            <span className={classes.subHeadLabel}>Add-ons</span>
            <span className={classes.subHeadRule} />
          </Box>
          {!sub.planKey && (
            <div className={classes.hint}>Add-ons need a base plan — choose a plan first.</div>
          )}

          {/* Every family is listed whether on or off, so the page says
              what a subscription can include up front. */}
          <Box className={classes.addonStack}>
            {ADDON_FAMILIES
              .filter((fam) => variantsAtDuration(catalog, fam, sub.duration).length > 0)
              .map((fam) => {
                const entry = addonOf(fam);
                const on = !!entry;
                // An add-on cannot exist without a base plan: block turning a
                // new one on (but still allow switching an existing one off).
                const addDisabled = !on && !sub.planKey;
                return (
                  <Box key={fam} className={`${classes.ent} ${on ? classes.entOn : ""} ${(locked || addDisabled) ? classes.entLocked : ""}`}>
                    <Box className={classes.entHead}
                      onClick={(locked || addDisabled) ? undefined : () => (on ? onRemoveAddon(fam) : onAddAddon(fam))}
                      style={{ cursor: (locked || addDisabled) ? "default" : "pointer" }}>
                      <Box className={classes.entIco} style={{ background: G_SOFT, color: "#0f7a3d" }}>
                        <AddIcon style={{ fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Box className={classes.entName}>{familyLabel(catalog, fam)}</Box>
                        <Box className={classes.entDesc}>{ADDON_DESC[fam]}</Box>
                      </Box>
                      <Box className={`${classes.sw} ${on ? classes.swOn : ""} ${(locked || addDisabled) ? classes.swDead : ""}`}>
                        <span className={`${classes.knob} ${on ? classes.knobOn : ""}`} />
                      </Box>
                    </Box>
                    {on && (
                      <Box className={classes.entBody}>
                        <AddonBody
                          family={fam} classes={classes} catalog={catalog} sub={sub} entry={entry}
                          locked={locked} lockMark={lockMark} showErrors={showErrors}
                          onPatchSub={onPatchSub} onSetQty={onSetAddonQty} onSetVariant={onSetAddonVariant} />
                      </Box>
                    )}
                  </Box>
                );
              })}
          </Box>
          <div className={classes.hint}>Priced in step 3.</div>
        </Box>
      )}
    </Box>
    )}

    {/* Credits — gated behind CHECKOUT_CREDITS_ENABLED for Phase-1. */}
    {entitlementsFor(form.type).includes("credits") && (
    <Box className={`${classes.ent} ${credits ? classes.entOn : ""} ${locked ? classes.entLocked : ""}`}>
      <Box className={classes.entHead} onClick={singleEnt || locked ? undefined : onToggleCredits}>
        <Box className={classes.entIco} style={{ background: G_SOFT, color: "#0f7a3d" }}><AccountBalanceWalletIcon style={{ fontSize: 20 }} /></Box>
        <Box>
          <Box className={classes.entName}>Credits
            <span className={classes.when} style={{ color: ACCOUNT, background: ACCOUNT_SOFT }}>after they redeem</span></Box>
          <Box className={classes.entDesc}>A one-time top-up added to the customer's balance.</Box>
        </Box>
        {!singleEnt && <Box className={`${classes.sw} ${credits ? classes.swOn : ""} ${locked ? classes.swDead : ""}`}><span className={`${classes.knob} ${credits ? classes.knobOn : ""}`} /></Box>}
      </Box>
      {credits && (
        <Box className={classes.entBody}>
          {creditFields.map((c) => (
            <Box key={c.k} className={classes.creditRow}>
              <div>
                <div className={classes.creditName}>{c.name}</div>
                <div className={classes.creditNote}>{c.note}</div>
              </div>
              <TextField className={`${classes.input} ${classes.numInput}`} type="number" variant="outlined" size="small"
                label="Quantity" placeholder="0" fullWidth
                inputProps={{ min: 0, step: 1, style: { textAlign: "right", fontVariantNumeric: "tabular-nums" } }}
                disabled={locked}
                value={credits[c.k] ?? ""} error={Number(credits[c.k]) < 0}
                onChange={(e) => onPatchCredits({ [c.k]: e.target.value === "" ? "" : Math.floor(Number(e.target.value)) })} />
            </Box>
          ))}
          <div className={classes.hint}>Priced in step 3.</div>
        </Box>
      )}
    </Box>
    )}

    {/* Webinar */}
    {allows(form.type, "webinar") && (
    <Box className={`${classes.ent} ${webinar ? classes.entOn : ""} ${locked ? classes.entLocked : ""}`}>
      <Box className={classes.entHead} onClick={singleEnt || locked ? undefined : onToggleWebinar}>
        <Box className={classes.entIco} style={{ background: CONTACT_SOFT, color: CONTACT }}><VideocamIcon style={{ fontSize: 20 }} /></Box>
        <Box>
          <Box className={classes.entName}>Webinar
            <span className={classes.when} style={{ color: CONTACT, background: CONTACT_SOFT }}>sent instantly</span></Box>
          <Box className={classes.entDesc}>A link emailed the moment payment succeeds — no login needed.</Box>
        </Box>
        {!singleEnt && <Box className={`${classes.sw} ${webinar ? classes.swOn : ""} ${locked ? classes.swDead : ""}`}><span className={`${classes.knob} ${webinar ? classes.knobOn : ""}`} /></Box>}
      </Box>
      {webinar && (
        <Box className={classes.entBody}>
          <div>
            <span className={classes.fieldLabel}>Meeting link</span>
            <TextField className={classes.input} fullWidth variant="outlined" size="small" placeholder="https://…"
              value={webinar.meetingLink} error={showErrors && !!shapeErr.webinar} helperText={showErrors ? shapeErr.webinar : ""}
              onChange={(e) => onPatchWebinar({ meetingLink: e.target.value })} />
          </div>
          <Box className={classes.grid2}>
            <div>
              <span className={classes.fieldLabel}>Email subject</span>
              <TextField className={classes.input} fullWidth variant="outlined" size="small"
                value={webinar.emailSubject} onChange={(e) => onPatchWebinar({ emailSubject: e.target.value })} />
            </div>
            <div>
              <span className={classes.fieldLabel}>WhatsApp template</span>
              <TextField className={classes.input} fullWidth variant="outlined" size="small"
                value={webinar.whatsappTemplateName} onChange={(e) => onPatchWebinar({ whatsappTemplateName: e.target.value })} />
            </div>
          </Box>
        </Box>
      )}
    </Box>
    )}

    {!singleEnt && <div className={classes.hint}>At least one entitlement is required.</div>}
    <IssueBanner issues={issues} classes={classes} />
    <Box className={classes.continueRow}>
      <Button onClick={onBack}>Back</Button>
      <Button className={classes.continueBtn} variant="contained" onClick={onContinue}>Continue</Button>
    </Box>
  </>
);

export default EntitlementsStep;
